import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Truck, Leaf, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { CATEGORIES } from "../mock";
import { DELIVERY_AREAS, findArea, FAQS } from "../data/seoContent";
import ProductCard from "../components/ProductCard";
import FaqSection from "../components/FaqSection";
import Seo from "../components/Seo";

const BASE_URL = "https://globaltreats.in";

const AreaPage = () => {
  const { area: slug } = useParams();
  const area = findArea(slug);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts().then((all) => {
      const best = all.filter((p) => p.bestseller);
      setProducts((best.length ? best : all).slice(0, 4));
    }).catch(() => setProducts([]));
  }, [slug]);

  if (!area) return <Navigate to="/collections/all" replace />;

  const otherAreas = DELIVERY_AREAS.filter((a) => a.slug !== slug);
  const popularCats = CATEGORIES.filter((c) => ["birthday", "anniversary", "designer", "kids", "chocolate", "wedding"].includes(c.slug));

  return (
    <div>
      <Seo
        path={`/cake-delivery/${area.slug}`}
        title={`Cake Delivery in ${area.name} — Order Online`}
        description={`Order fresh, hand-decorated cakes online for delivery in ${area.name}. Birthday, anniversary, designer, eggless, photo & custom cakes with next-day delivery from Globaltreats.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Online Cake Delivery",
          provider: { "@type": "Bakery", name: "Globaltreats", telephone: "+91-92649-71235", url: BASE_URL },
          areaServed: { "@type": "Place", name: area.name },
          url: `${BASE_URL}/cake-delivery/${area.slug}`,
          description: `Fresh cake delivery in ${area.name}, ${area.locality}. Birthday, anniversary, designer, eggless, photo and custom cakes with next-day delivery.`,
        }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#FCEFE3] to-[#F7DCE4]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-2 bg-white/70 text-[#6B4226] text-sm px-4 py-1.5 rounded-full mb-5">
            <MapPin size={14} className="text-[#D46A8A]" /> Now delivering to {area.name}
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-[#3A2417] leading-tight">
            Cake Delivery in {area.name}
          </h1>
          <p className="text-[#6B4226]/80 text-lg mt-5 max-w-2xl mx-auto">
            Order freshly baked, hand-decorated cakes online for delivery in {area.name}. From birthday and anniversary cakes to eggless, designer, photo and fully custom creations — delivered fresh, next-day, right to your doorstep.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <Link to="/collections/all" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors" data-testid="area-order-btn">
              Order Now <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-[#6B4226] font-medium hover:bg-[#FCEFE3] transition-colors">Request a Custom Cake</Link>
          </div>
        </div>
      </section>

      {/* Why us in this area */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14">
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: Truck, t: "Next-day delivery", s: `Reliable, fresh cake delivery across ${area.name} and nearby ${area.locality} sectors.` },
            { icon: Leaf, t: "Eggless options", s: "Every flavour and design is available 100% eggless at no extra cost." },
            { icon: Clock, t: "Baked fresh to order", s: "Nothing pre-made — your cake is baked close to your chosen delivery date." },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 bakery-shadow">
              <div className="w-12 h-12 rounded-full bg-[#F7DCE4] grid place-items-center mb-4"><f.icon size={22} className="text-[#D46A8A]" /></div>
              <h3 className="font-display text-lg font-semibold text-[#3A2417]">{f.t}</h3>
              <p className="text-sm text-[#6B4226] mt-1.5">{f.s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Popular categories */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-6">
        <h2 className="font-display text-2xl md:text-3xl font-bold text-[#3A2417] mb-6">Popular cakes in {area.name}</h2>
        <div className="flex flex-wrap gap-3">
          {popularCats.map((c) => (
            <Link key={c.slug} to={`/collections/${c.slug}`} className="inline-flex items-center gap-2 bg-white border border-[#EADBCB] rounded-full px-5 h-11 text-sm text-[#6B4226] hover:border-[#D46A8A] hover:text-[#D46A8A] transition-colors">
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      {products.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Delivery zone note */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="bg-[#EAF3E4] rounded-2xl p-6 flex items-start gap-3">
          <CheckCircle2 size={22} className="text-[#6BA84F] mt-0.5 shrink-0" />
          <p className="text-[#3f6b2e] text-sm">
            <strong>{area.name} is within our delivery zone.</strong> We deliver across {area.locality} and the wider Noida & Greater Noida region. Just enter your pincode at checkout to confirm and pick your delivery date.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection
        title={`Cake Delivery in ${area.name} — FAQs`}
        subtitle={`Common questions about ordering cakes for delivery in ${area.name}.`}
      />

      {/* Internal links to other areas */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-16">
        <h2 className="font-display text-xl font-semibold text-[#3A2417] mb-4">We also deliver cakes to</h2>
        <div className="flex flex-wrap gap-2.5">
          {otherAreas.map((a) => (
            <Link key={a.slug} to={`/cake-delivery/${a.slug}`} className="inline-flex items-center gap-1.5 bg-white border border-[#EADBCB] rounded-full px-4 py-2 text-sm text-[#6B4226] hover:border-[#D46A8A] hover:text-[#D46A8A] transition-colors" data-testid={`area-link-${a.slug}`}>
              <MapPin size={13} /> {a.name}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AreaPage;
